'use client';

import * as React from 'react';

import { ArrowLeft, ArrowRight } from 'lucide-react';
import {
  Carousel as ShadcnCarousel,
  CarouselContent as ShadcnCarouselContent,
  CarouselItem as ShadcnCarouselItem,
  CarouselNext as ShadcnCarouselNext,
  CarouselPrevious as ShadcnCarouselPrevious,
  useCarousel,
  type CarouselApi,
} from '@/hen-ui/shadcn/carousel';
import { Button as ShadcnButton } from '@/hen-ui/shadcn/button';
import {
  carouselItemVariants,
  carouselNavButtonVariants,
  carouselArrowHitAreaClassName,
  carouselArrowInsetClasses,
  carouselShadowSpaceClasses,
  carouselShadowSpaceVerticalClasses,
  carouselDotVariants,
  carouselDotsTrackVariants,
  carouselDotsBackgroundVariants,
  carouselPillDotCellVariants,
  carouselCompactDotCellClassName,
  type CarouselArrowDesign,
  type CarouselArrowInset,
  type CarouselArrowPosition,
  type CarouselArrowReveal,
  type CarouselUnavailableArrow,
  type CarouselArrowSize,
  type CarouselDotDesign,
  type CarouselDotSize,
  type CarouselDotTone,
  type CarouselShadowSpace,
  type CarouselSlideHeight,
  type CarouselItemVariantProps,
  type CarouselNavButtonVariantProps,
  type CarouselDotVariantProps,
} from './carousel.variants';
import { cn } from '@/hen-ui/utils';
import {
  carouselItemPeekClassName,
  carouselNextPeekRootClass,
  fractionRowExceedsPage,
  isCarouselNextPeekEnabled,
  normalizeCarouselNextPeek,
  peekActiveAt,
  peekSlideProps,
  type CarouselNextPeek,
  type FractionRowExceeds,
} from './carouselPeek';
import { useGroupedSlidesPerView } from './useGroupedCarousel';
import { resolveVisibleDotWindow } from './resolveVisibleDotWindow';

export { resolveVisibleDotWindow } from './resolveVisibleDotWindow';

export { CAROUSEL_NEXT_PEEK, type CarouselNextPeek } from './carouselPeek';

export {
  CAROUSEL_ARROW_INSET,
  CAROUSEL_ARROW_POSITION,
  CAROUSEL_ARROW_REVEAL,
  CAROUSEL_UNAVAILABLE_ARROW,
  CAROUSEL_DOT_DESIGNS,
  CAROUSEL_DOT_SIZES,
  CAROUSEL_DOT_TONES,
  CAROUSEL_SHADOW_SPACE,
} from './carousel.variants';

export type {
  CarouselArrowDesign,
  CarouselArrowInset,
  CarouselArrowPosition,
  CarouselArrowReveal,
  CarouselUnavailableArrow,
  CarouselArrowSize,
  CarouselDotDesign,
  CarouselDotSize,
  CarouselDotTone,
  CarouselShadowSpace,
  CarouselSlideHeight,
  CarouselItemBasisToken,
  CarouselItemBasisBreakpointToken,
} from './carousel.variants';

const inFlowArrowClassName = 'relative inset-auto translate-y-0';

const besideArrowClassName = 'shrink-0 max-md:hidden';

const headerArrowCellClassName =
  'flex min-h-11 min-w-11 shrink-0 items-center justify-center';

/** Where the active slide sits in the viewport. */
export const CAROUSEL_SLIDE_ALIGN = ['center', 'start'] as const;

/** How far each arrow, dot, and swipe moves. `slide` is the saved default. */
export const CAROUSEL_PAGING = ['slide', 'group'] as const;

export type CarouselPaging = (typeof CAROUSEL_PAGING)[number];

/** Where pagination dots render. `auto` overlays pill dots and puts others below. */
export const CAROUSEL_DOT_POSITION = ['auto', 'overlay', 'below'] as const;

export type CarouselDotPosition = (typeof CAROUSEL_DOT_POSITION)[number];

export type CarouselSlideAlign = (typeof CAROUSEL_SLIDE_ALIGN)[number];

const CarouselSlideHeightContext =
  React.createContext<CarouselSlideHeight>('auto');

const CarouselNextPeekContext = React.createContext<{
  active: boolean;
  peekOnPhone: boolean;
  slideCount: number;
  rowExceeds: FractionRowExceeds;
  registerSlide: (
    id: string,
    slide: {
      basis?: unknown;
      'basis-md'?: unknown;
      'basis-lg'?: unknown;
    } | null,
  ) => void;
}>({
  active: false,
  peekOnPhone: false,
  slideCount: 0,
  rowExceeds: { base: false, md: false, lg: false },
  registerSlide: () => {},
});

const PEEK_ALIGN_QUERY = {
  md: '(min-width: 768px)',
  lg: '(min-width: 1024px)',
} as const;

type PeekViewport = 'base' | 'md' | 'lg';

/**
 * Current breakpoint, using the same md and lg widths as the peek classes.
 *
 * @returns `lg` at 1024px, `md` at 768px, otherwise `base`
 */
function readPeekViewport(): PeekViewport {
  if (
    typeof window === 'undefined' ||
    typeof window.matchMedia !== 'function'
  ) {
    return 'base';
  }
  if (window.matchMedia(PEEK_ALIGN_QUERY.lg).matches) return 'lg';
  if (window.matchMedia(PEEK_ALIGN_QUERY.md).matches) return 'md';
  return 'base';
}

/**
 * Pins to the start only while the peek is changing width at this screen.
 * A later card or neighbor-peek width releases the pin.
 *
 * @param active - Peek state at base, md, and lg
 * @returns Whether Embla should pin slides to the start
 */
function usePeekAlign(active: {
  base: boolean;
  md: boolean;
  lg: boolean;
}): boolean {
  const [, redraw] = React.useState(0);

  React.useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const md = window.matchMedia(PEEK_ALIGN_QUERY.md);
    const lg = window.matchMedia(PEEK_ALIGN_QUERY.lg);
    const update = () => redraw((count) => count + 1);
    md.addEventListener('change', update);
    lg.addEventListener('change', update);
    return () => {
      md.removeEventListener('change', update);
      lg.removeEventListener('change', update);
    };
  }, []);

  // Read during render. The server has no window, so it returns base, and
  // that value is what a stored useState would hydrate. Embla then inits
  // from a child effect, which runs before a parent effect can correct it.
  // Alignment is not written into the markup, so the client read does not
  // change the server HTML.
  return active[readPeekViewport()];
}

const insideArrowClassName = 'absolute top-1/2 z-10';

const hoverRevealClassName =
  'transition-opacity [.group:not(:hover)_&:not(:focus-visible)]:pointer-events-none [.group:not(:hover)_&:not(:focus-visible)]:opacity-0 [&[aria-disabled=true]:not(:focus-visible)]:opacity-0 [@media(hover:none)]:hidden';

export interface CarouselProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof ShadcnCarousel>,
    'children' | 'orientation'
  > {
  /** Direct slide content. Use CarouselItem for each slide. */
  children: React.ReactNode;
  showControls?: boolean;
  showDots?: boolean;
  /**
   * Let visitors select a page by pressing its dot. Turn off to show the dots
   * as position indicators while navigation stays with arrows, keyboard, and
   * swipe. Omitted means true.
   */
  dotsInteractive?: boolean;
  /**
   * Where dots render. `auto` overlays pill dots and puts solid or outline
   * dots below the slides. Choose `below` to keep pill indicators outside the
   * slide media. Omitted means `auto`.
   */
  dotPosition?: CarouselDotPosition;
  /**
   * Pagination-dot look. `default` and `outline` stay in the footer row under
   * the slides. `pill` overlays circular inactive dots and an elongated active
   * pill on the media. Omitted means `default`.
   */
  dotDesign?: CarouselDotDesign;
  /**
   * Show a rounded capsule behind the dots so they stay readable on photos:
   * dark behind `light` dots, light behind `dark` dots. Defaults to on when
   * `dotDesign` is `pill`, and off otherwise. Set explicitly to force show or
   * hide.
   */
  showDotBackground?: boolean;
  /**
   * Dot color for `pill` (ignored by `default` / `outline`).
   * `light` = white glyphs for dark media; `dark` = charcoal glyphs for light
   * surfaces. Omitted means `light`.
   */
  dotTone?: CarouselDotTone;
  /**
   * Pagination-dot size. `default` keeps today's dots. `compact` draws a
   * shorter active pill and smaller inactive dots, and shortens the pill
   * track. Every size keeps a 24px tap target. Omitted means `default`.
   */
  dotSize?: CarouselDotSize;
  /**
   * Maximum number of pagination dots to show, from 1 to 5. Values above 5
   * show 5; blank, 0, or negative values show one dot per page/slide. When
   * there are more pages than this, the dots scroll with the active page.
   * Any valid value also gives `pill` dots the capped look, even when every
   * page fits: compact inactive dots match the active height, and dots
   * animate between pages.
   */
  maxVisibleDots?: number;
  /**
   * `slide` moves one slide at a time. `group` moves every slide that fits,
   * and the dots match those groups, including a shorter last group.
   * Grouped paging pins slides to the start and does not loop, so a short
   * last group stays reachable. `loop` and `slideAlign` apply only when
   * paging is `slide`. Omitted means `slide`, which keeps saved carousels
   * unchanged.
   */
  paging?: CarouselPaging;
  /**
   * How much of the next slide stays visible on the right. `none` keeps
   * today's widths. A size applies when the row is wider than the page, for
   * two, three, four, or five slides per view, and only when looping is off
   * (grouped paging already turns looping off). The first position stays
   * flush left at the widths where that shrink is on. A later width that
   * does not shrink uses `slideAlign` again. The last position ends flush
   * right. One page of slides, a single full-width slide, and fixed card
   * widths are unchanged. A row that mixes a fraction with a card or the
   * 88% width still uses the slide count, because those widths cannot be
   * added up. Half stays one full slide on a phone unless `peekOnPhone`
   * is set. Omitted means `none`.
   */
  peek?: CarouselNextPeek;
  /**
   * On a phone, a half-width row is one full slide. Set this to keep the
   * peek slice visible there too. It uses the size in `peek`, and it does
   * nothing when `peek` is `none` or omitted. Larger screens stay the same.
   * Omitted means off.
   */
  peekOnPhone?: boolean;
  /**
   * When arrows are on, keep them visible or hide them until hover or focus.
   * Has no effect when `showControls` is false.
   */
  arrowReveal?: CarouselArrowReveal;
  /**
   * What happens to an arrow that cannot move. `dim` keeps it on the page.
   * `hide` removes it, and the arrow that can move stays. Omitted means `dim`,
   * so saved carousels stay unchanged. A looping carousel never hides an arrow,
   * because both directions can move.
   */
  unavailableArrow?: CarouselUnavailableArrow;
  /**
   * When arrows are on, keep them in the footer row, place them inside the slide
   * edges, place them beside the slides, or place them in a header row above
   * the slides. Below the `md` breakpoint, beside arrows are hidden and swipe
   * takes over. When `showControls` is false, `header` still shows the `header`
   * heading above the slides; the other positions have no effect.
   */
  arrowPosition?: CarouselArrowPosition;
  /**
   * Horizontal padding between inside Previous/Next arrows and the slide edges.
   * Only applies when `arrowPosition` is `inside`. Omitted means `none`
   * (flush with the edges), which keeps saved carousels unchanged.
   */
  arrowInset?: CarouselArrowInset;
  /**
   * Heading above the slides, rendered only when `arrowPosition` is `header`.
   * With arrows on, it starts the header row and the arrows trail it; with
   * `showControls` false, the heading stays and the arrows are omitted.
   */
  header?: React.ReactNode;
  /**
   * Optional chrome above the slides when arrows are beside them. Below `md`
   * it renders at the slide width. From `md` the stage is a three-column grid
   * (previous arrow, slides, next arrow) and this wrapper uses
   * `display: contents`, so children can take `md:col-start-1` (arrow column)
   * or `md:col-start-2` (slide column) on `md:row-start-1`.
   */
  besideStart?: React.ReactNode;
  /**
   * Arrow fill and icon colors. `onDark` is a white circle with a dark icon for
   * dark surfaces; `solid` is a black circle with a white icon. Has no effect
   * when `showControls` is false.
   */
  arrowDesign?: CarouselArrowDesign;
  /**
   * Arrow circle size: 24px, 36px, 44px, or 48px. Every size keeps a 44px touch target.
   * Has no effect when `showControls` is false.
   */
  arrowSize?: CarouselArrowSize;
  /**
   * Where the active slide sits. `center` (default) centers it, so narrower
   * slides show partial neighbors on both sides. `start` pins it to the left
   * edge, so two half-width slides fill the viewport side by side.
   */
  slideAlign?: CarouselSlideAlign;
  /**
   * `auto` (default) lets each slide's content keep its own height. `equal`
   * stretches each slide's content to the tallest slide, so a row of cards
   * shares one height.
   */
  slideHeight?: CarouselSlideHeight;
  /** Room around the slides so card shadows aren't clipped: `sm` 8px, `md` 16px.
   * Above and below only with `beside` arrows; ignored in the photo-viewer layout. */
  shadowSpace?: CarouselShadowSpace;
  /**
   * Extra classes for the Embla viewport (the `overflow-hidden` element).
   * Pass a padding + negative-margin pair (e.g. `-my-2 py-2`) when slides carry
   * borders or hover shadows so the viewport doesn't clip them.
   */
  viewportClassName?: string;
}

export interface CarouselItemProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnCarouselItem>,
    CarouselItemVariantProps {
  children?: React.ReactNode;
}

type ArrowDirection = 'previous' | 'next';

type PendingArrowFocus = ArrowDirection | 'region';

type ArrowVisibility = {
  showPrevious: boolean;
  showNext: boolean;
  previousRef: React.MutableRefObject<HTMLButtonElement | null>;
  nextRef: React.MutableRefObject<HTMLButtonElement | null>;
};

const ArrowVisibilityContext = React.createContext<ArrowVisibility | null>(
  null,
);

/**
 * Reads whether each arrow can move. Must render inside the carousel.
 *
 * @returns Which arrows are mounted, plus the button refs used to move focus
 */
function useArrowVisibility(): ArrowVisibility {
  const value = React.useContext(ArrowVisibilityContext);
  if (!value) {
    throw new Error('Carousel arrows must render inside the carousel');
  }
  return value;
}

/**
 * Moves focus to the carousel region when both arrows are removed.
 *
 * @param region - The carousel region element
 */
function focusCarouselRegion(region: HTMLElement | null): void {
  region?.focus();
}

/**
 * Decides which arrows stay mounted and where focus goes when one unmounts.
 * `dim` always mounts both. `hide` mounts an arrow only while it can move.
 * Before the carousel has measured, both stay mounted so they do not flash away.
 */
function CarouselArrowVisibility({
  unavailableArrow,
  regionRef,
  children,
}: {
  unavailableArrow: CarouselUnavailableArrow;
  regionRef: React.MutableRefObject<HTMLDivElement | null>;
  children: React.ReactNode;
}) {
  const { api } = useCarousel();
  const hideUnavailable = unavailableArrow === 'hide';
  const canScrollPrev = api ? api.canScrollPrev() : true;
  const canScrollNext = api ? api.canScrollNext() : true;
  const showPrevious = !hideUnavailable || canScrollPrev;
  const showNext = !hideUnavailable || canScrollNext;
  const previousRef = React.useRef<HTMLButtonElement | null>(null);
  const nextRef = React.useRef<HTMLButtonElement | null>(null);
  const pendingFocus = React.useRef<PendingArrowFocus | null>(null);

  // The arrow is still focused during this render. Record the next target
  // before commit removes the button and the browser drops focus.
  // `document` is absent while the carousel is rendered on the server.
  if (hideUnavailable && typeof document !== 'undefined') {
    const active = document.activeElement;
    if (
      !showPrevious &&
      previousRef.current !== null &&
      active === previousRef.current
    ) {
      pendingFocus.current = showNext ? 'next' : 'region';
    } else if (
      !showNext &&
      nextRef.current !== null &&
      active === nextRef.current
    ) {
      pendingFocus.current = showPrevious ? 'previous' : 'region';
    }
  }

  React.useLayoutEffect(() => {
    const target = pendingFocus.current;
    if (!target) {
      return;
    }
    pendingFocus.current = null;
    if (target === 'previous') {
      previousRef.current?.focus();
      return;
    }
    if (target === 'next') {
      nextRef.current?.focus();
      return;
    }
    focusCarouselRegion(regionRef.current);
  }, [regionRef, showPrevious, showNext]);

  const value = React.useMemo<ArrowVisibility>(
    () => ({
      showPrevious,
      showNext,
      previousRef,
      nextRef,
    }),
    [showNext, showPrevious],
  );

  return (
    <ArrowVisibilityContext.Provider value={value}>
      {children}
    </ArrowVisibilityContext.Provider>
  );
}

/**
 * Previous or Next, omitted when `hide` and that direction cannot move.
 */
function CarouselDirectionButton({
  direction,
  className,
}: {
  direction: ArrowDirection;
  className: string;
}) {
  const visibility = useArrowVisibility();
  if (direction === 'previous') {
    if (!visibility.showPrevious) {
      return null;
    }
    return (
      <ShadcnCarouselPrevious
        ref={visibility.previousRef as React.Ref<HTMLButtonElement>}
        className={className}
      />
    );
  }
  if (!visibility.showNext) {
    return null;
  }
  return (
    <ShadcnCarouselNext
      ref={visibility.nextRef as React.Ref<HTMLButtonElement>}
      className={className}
    />
  );
}

/**
 * Header arrow pair. A hidden arrow takes its cell with it so the row does not
 * keep an empty gap.
 */
function HeaderArrowCluster({ className }: { className: string }) {
  const { showPrevious, showNext } = useArrowVisibility();
  if (!showPrevious && !showNext) {
    return null;
  }
  return (
    <>
      {/* hen-ui-allow-raw-html -- keeps Previous and Next together on the trailing edge when the heading wraps */}
      <div className='ml-auto flex shrink-0 items-center gap-sm'>
        {showPrevious ? (
          <>
            {/* hen-ui-allow-raw-html -- cell at least as large as the 44px hit area so it neither overlaps Next nor overflows the row */}
            <span className={headerArrowCellClassName}>
              <CarouselDirectionButton
                direction='previous'
                className={className}
              />
            </span>
          </>
        ) : null}
        {showNext ? (
          <>
            {/* hen-ui-allow-raw-html -- cell at least as large as the 44px hit area so it stays inside the trailing edge */}
            <span className={headerArrowCellClassName}>
              <CarouselDirectionButton direction='next' className={className} />
            </span>
          </>
        ) : null}
      </div>
    </>
  );
}

function CarouselHeaderRow({
  heading,
  arrowClassName,
}: {
  heading: React.ReactNode;
  arrowClassName: string;
}) {
  const { showPrevious, showNext } = useArrowVisibility();
  if (!heading && !showPrevious && !showNext) {
    return null;
  }
  return (
    <>
      {/* hen-ui-allow-raw-html -- header row places the heading and in-flow arrows above the slides */}
      <div className='flex min-w-0 flex-wrap items-center justify-between gap-sm'>
        {heading}
        <HeaderArrowCluster className={arrowClassName} />
      </div>
    </>
  );
}

function CarouselFooterNavigation({
  arrowsInFooter,
  footerDots,
  arrowClassName,
  dotDesign,
  dotTone,
  dotSize,
  maxVisibleDots,
  dotsInteractive,
  showBackground,
}: {
  arrowsInFooter: boolean;
  footerDots: boolean;
  arrowClassName: string;
  dotDesign: CarouselDotDesign;
  dotTone: CarouselDotTone;
  dotSize: CarouselDotSize;
  maxVisibleDots?: number;
  dotsInteractive: boolean;
  showBackground: boolean;
}) {
  const { showPrevious, showNext } = useArrowVisibility();
  const mountPrevious = arrowsInFooter && showPrevious;
  const mountNext = arrowsInFooter && showNext;
  if (!mountPrevious && !mountNext && !footerDots) {
    return null;
  }
  return (
    <div className='flex items-center justify-center gap-4'>
      {mountPrevious ? (
        <CarouselDirectionButton
          direction='previous'
          className={arrowClassName}
        />
      ) : null}
      {footerDots ? (
        <CarouselDotsConnected
          design={dotDesign}
          tone={dotTone}
          size={dotSize}
          maxVisibleDots={maxVisibleDots}
          interactive={dotsInteractive}
          showBackground={showBackground}
        />
      ) : null}
      {mountNext ? (
        <CarouselDirectionButton direction='next' className={arrowClassName} />
      ) : null}
    </div>
  );
}

/**
 * Carousel component following HEN Design System standards.
 *
 * A high-level carousel component that automatically includes navigation controls.
 * Children should be CarouselItem components.
 *
 * @example
 * // Basic usage
 * <Carousel>
 *   <CarouselItem><Hero /></CarouselItem>
 *   <CarouselItem><Hero /></CarouselItem>
 *   <CarouselItem><Hero /></CarouselItem>
 * </Carousel>
 *
 * @example
 * // Dots without arrows
 * <Carousel showControls={false} showDots={true}>
 *   <CarouselItem>Slide 1</CarouselItem>
 * </Carousel>
 *
 * @example
 * // Footer arrows revealed on hover
 * <Carousel arrowReveal="hover">
 *   <CarouselItem>Slide 1</CarouselItem>
 * </Carousel>
 *
 * @example
 * // Remove the arrow that cannot move
 * <Carousel loop={false} unavailableArrow="hide">
 *   <CarouselItem>Slide 1</CarouselItem>
 * </Carousel>
 *
 * @example
 * // Arrows inside the slide edges
 * <Carousel arrowPosition="inside">
 *   <CarouselItem>Slide 1</CarouselItem>
 * </Carousel>
 *
 * @example
 * // Pill indicators with dark track + inset inside arrows
 * <Carousel
 *   arrowPosition="inside"
 *   arrowInset="sm"
 *   arrowDesign="onDark"
 *   dotDesign="pill"
 *   dotTone="light"
 *   showDotBackground
 *   showControls
 * >
 *   <CarouselItem basis="full">Photo</CarouselItem>
 * </Carousel>
 *
 * @example
 * // Arrows above the slides, trailing an authored heading
 * <Carousel arrowPosition="header" header={<h2>Special offers</h2>}>
 *   <CarouselItem>Slide 1</CarouselItem>
 * </Carousel>
 *
 * @example
 * // Photo viewer on a dark surface: stops at the ends, large arrows beside the slide
 * <Carousel loop={false} arrowPosition="beside" arrowDesign="onDark" arrowSize="lg">
 *   <CarouselItem>Slide 1</CarouselItem>
 * </Carousel>
 *
 * @example
 * // Five slides on a desktop, small cards on a phone, advancing by group
 * <Carousel paging="group">
 *   <CarouselItem basis="card-sm" basis-lg="fifth">Card</CarouselItem>
 * </Carousel>
 */
export const Carousel = React.forwardRef<HTMLDivElement, CarouselProps>(
  (
    {
      className,
      children,
      showControls = true,
      showDots = true,
      dotsInteractive = true,
      dotPosition = 'auto',
      dotDesign = 'default',
      showDotBackground,
      dotTone = 'light',
      dotSize = 'default',
      maxVisibleDots,
      arrowReveal = 'always',
      unavailableArrow = 'dim',
      arrowPosition = 'bottom',
      arrowInset = 'none',
      header,
      besideStart,
      arrowDesign = 'default',
      arrowSize = 'sm',
      loop,
      slideAlign = 'center',
      slideHeight = 'auto',
      shadowSpace = 'none',
      viewportClassName,
      paging = 'slide',
      peek,
      peekOnPhone = false,
      opts,
      setApi: setApiProp,
      tabIndex,
      ...props
    },
    ref,
  ) => {
    const regionRef = React.useRef<HTMLDivElement | null>(null);
    const setRegionRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        regionRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
          return;
        }
        if (ref) {
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
      },
      [ref],
    );
    const [api, setApi] = React.useState<CarouselApi>();
    const perView = useGroupedSlidesPerView(api, paging === 'group');
    const grouped = paging === 'group';
    const peekValue = normalizeCarouselNextPeek(peek);
    const treeSlides = peekSlideProps(children);
    const reportedSlidesRef = React.useRef(
      new Map<
        string,
        {
          basis?: unknown;
          'basis-md'?: unknown;
          'basis-lg'?: unknown;
        }
      >(),
    );
    const [reportedTick, setReportedTick] = React.useState(0);
    const registerSlide = React.useCallback(
      (
        id: string,
        slide: {
          basis?: unknown;
          'basis-md'?: unknown;
          'basis-lg'?: unknown;
        } | null,
      ) => {
        const reported = reportedSlidesRef.current;
        if (slide == null) {
          if (!reported.delete(id)) return;
        } else {
          const current = reported.get(id);
          if (
            current &&
            current.basis === slide.basis &&
            current['basis-md'] === slide['basis-md'] &&
            current['basis-lg'] === slide['basis-lg']
          ) {
            return;
          }
          reported.set(id, slide);
        }
        setReportedTick((tick) => tick + 1);
      },
      [],
    );
    const peekSlides =
      reportedTick > 0
        ? Array.from(reportedSlidesRef.current.values())
        : treeSlides;
    const slideCount = peekSlides.length;
    const resolvedLoop = loop ?? opts?.loop ?? true;
    const peekEnabled = isCarouselNextPeekEnabled({
      peek: peekValue,
      loop: resolvedLoop,
      paging,
    });
    const measuredRow = fractionRowExceedsPage(peekSlides);
    const rowExceeds = React.useMemo(
      () => ({
        base: measuredRow.base,
        md: measuredRow.md,
        lg: measuredRow.lg,
      }),
      [measuredRow.base, measuredRow.md, measuredRow.lg],
    );
    const peekContext = React.useMemo(
      () => ({
        active: peekEnabled,
        peekOnPhone,
        slideCount,
        rowExceeds,
        registerSlide,
      }),
      [peekEnabled, peekOnPhone, slideCount, rowExceeds, registerSlide],
    );
    const peekAt = peekSlides.reduce(
      (active, item) => {
        if (!peekEnabled) return active;
        const next = peekActiveAt({
          basis: item.basis,
          'basis-md': item['basis-md'],
          'basis-lg': item['basis-lg'],
          slideCount,
          rowExceeds,
          peekOnPhone,
        });
        return {
          base: active.base || next.base,
          md: active.md || next.md,
          lg: active.lg || next.lg,
        };
      },
      { base: false, md: false, lg: false },
    );
    const pinPeekAlign = usePeekAlign(peekAt);
    const resolvedOpts = React.useMemo(() => {
      if (!grouped) {
        return pinPeekAlign
          ? { ...opts, align: 'start' as const }
          : { align: slideAlign, ...opts };
      }
      return {
        ...opts,
        align: 'start' as const,
        loop: false,
        containScroll: 'keepSnaps' as const,
        slidesToScroll: perView,
      };
    }, [grouped, opts, pinPeekAlign, perView, slideAlign]);
    const handleApi = React.useCallback(
      (next: CarouselApi) => {
        setApi(next);
        setApiProp?.(next);
      },
      [setApiProp],
    );
    const arrowsInside = showControls && arrowPosition === 'inside';
    const arrowsBeside = showControls && arrowPosition === 'beside';
    const arrowsInHeader = showControls && arrowPosition === 'header';
    const arrowsInFooter =
      showControls && !arrowsInside && !arrowsBeside && !arrowsInHeader;
    const resolvedDotPosition =
      dotPosition === 'auto'
        ? dotDesign === 'pill'
          ? 'overlay'
          : 'below'
        : dotPosition;
    const overlayDots = showDots && resolvedDotPosition === 'overlay';
    const footerDots = showDots && !overlayDots;
    const compactGroupedPillFooter =
      grouped && footerDots && dotDesign === 'pill';
    const needsKeyboardTarget = !showControls && showDots && !dotsInteractive;
    // Pill defaults the track on; only an explicit `false` hides it. Other
    // designs default off and need an explicit `true` (Storybook booleans
    // often pass `false` instead of `undefined`, which would break `??`).
    const resolvedShowDotBackground =
      dotDesign === 'pill'
        ? showDotBackground !== false
        : showDotBackground === true;
    const revealClassName =
      arrowReveal === 'hover' ? hoverRevealClassName : undefined;
    const arrowClassName = cn(
      carouselNavButtonVariants({ design: arrowDesign, size: arrowSize }),
      carouselArrowHitAreaClassName,
      // onDark beside arrows sit over a dismissible viewer backdrop. Keep
      // unavailable ones pointer-reachable so the press is not treated as a
      // dark-field miss (shadcn defaults to pass-through). Scoped to onDark
      // so other designs keep aria-disabled:pointer-events-none and no hover.
      arrowsBeside &&
        arrowDesign === 'onDark' &&
        'aria-disabled:pointer-events-auto',
    );
    const inFlowClassName = cn(
      arrowClassName,
      inFlowArrowClassName,
      revealClassName,
    );
    const besideClassName = cn(inFlowClassName, besideArrowClassName);
    // DSL values can be null, misspelled, or unresolved bindings at runtime.
    const insideInset =
      carouselArrowInsetClasses[arrowInset] ?? carouselArrowInsetClasses.none;
    const shadowRoom =
      shadowSpace &&
      Object.prototype.hasOwnProperty.call(
        carouselShadowSpaceClasses,
        shadowSpace,
      )
        ? (arrowsBeside
            ? carouselShadowSpaceVerticalClasses
            : carouselShadowSpaceClasses)[shadowSpace]
        : '';
    const overlayDotsNode = overlayDots ? (
      // hen-ui-allow-raw-html -- pins the pagination indicator track over the media
      <div className='pointer-events-none absolute inset-x-0 bottom-sm z-10 flex justify-center'>
        <div className='pointer-events-auto'>
          <CarouselDotsConnected
            design={dotDesign}
            tone={dotTone}
            size={dotSize}
            maxVisibleDots={maxVisibleDots}
            interactive={dotsInteractive}
            showBackground={resolvedShowDotBackground}
          />
        </div>
      </div>
    ) : null;
    const headerHeading =
      arrowPosition === 'header' && header ? (
        <>
          {/* hen-ui-allow-raw-html -- lets a long heading wrap without pushing the arrows off the page; an empty slot collapses so it adds no gap */}
          <div className='min-w-0 flex-1 empty:hidden'>{header}</div>
        </>
      ) : null;

    return (
      <ShadcnCarousel
        className={cn(
          'group relative w-full min-w-0 flex flex-col',
          compactGroupedPillFooter ? 'gap-md' : 'gap-lg md:gap-d3 lg:gap-2xl',
          peekEnabled && carouselNextPeekRootClass(peekValue),
          peekEnabled &&
            arrowsBeside &&
            besideStart &&
            '[--carousel-slide-gutter:0px]',
          needsKeyboardTarget &&
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 focus-visible:ring-offset-2',
          className,
        )}
        ref={setRegionRef}
        loop={grouped ? false : loop}
        opts={resolvedOpts}
        setApi={handleApi}
        {...props}
        tabIndex={
          tabIndex ?? (needsKeyboardTarget ? 0 : showControls ? -1 : undefined)
        }
      >
        <CarouselArrowVisibility
          unavailableArrow={unavailableArrow}
          regionRef={regionRef}
        >
          {arrowsInHeader ? (
            <CarouselHeaderRow
              heading={headerHeading}
              arrowClassName={inFlowClassName}
            />
          ) : (
            headerHeading
          )}
          {arrowsBeside && besideStart ? (
            <>
              {/* hen-ui-allow-raw-html -- centers the beside stage in the full carousel width */}
              <div className='relative flex h-full min-h-0 w-full min-w-0 justify-center'>
                {/* hen-ui-allow-raw-html -- flex column below md, [arrow][photo][arrow] grid from md; Grid's default gap and columns would fight the arbitrary template */}
                <div
                  data-slot='carousel-beside-stage'
                  className='flex h-full min-h-0 w-max max-w-full flex-col items-center md:grid md:h-auto md:grid-cols-[auto_var(--carousel-photo-width,auto)_auto] md:items-center md:gap-x-sm md:gap-y-xl'
                >
                  {/* hen-ui-allow-raw-html -- photo-width chrome below md; display: contents from md so its children join the stage grid */}
                  <div
                    data-slot='carousel-beside-chrome'
                    className='relative z-10 w-[var(--carousel-photo-width,auto)] max-w-full shrink-0 overflow-visible md:contents'
                  >
                    {besideStart}
                  </div>
                  {/* hen-ui-allow-raw-html -- arrow row below md; display: contents from md so the arrows and viewport join the stage grid */}
                  <div
                    data-slot='carousel-beside-row'
                    className='flex min-h-0 w-max max-w-full flex-1 items-center gap-sm md:contents'
                  >
                    <CarouselDirectionButton
                      direction='previous'
                      className={cn(
                        besideClassName,
                        'md:col-start-1 md:row-start-2',
                      )}
                    />
                    {/* hen-ui-allow-raw-html -- slide viewport matches the chrome width.
                      Embla can paint a sub-pixel strip of the previous/next
                      slide at each edge after translate; clip + paint
                      containment trim it, and the 2px masks match the viewer
                      backdrop so any remaining hairline stays invisible. */}
                    <div
                      data-slot='carousel-beside-viewport'
                      className='relative flex min-h-0 min-w-0 w-[var(--carousel-photo-width,auto)] max-w-full flex-col justify-center overflow-hidden [clip-path:inset(0_2px)] [contain:paint] md:col-start-2 md:row-start-2'
                    >
                      {/* hen-ui-allow-raw-html -- covers left-edge Embla hairline */}
                      <div
                        aria-hidden
                        className='pointer-events-none absolute inset-y-0 left-0 z-20 w-0.5 bg-neutral-900'
                      />
                      {/* hen-ui-allow-raw-html -- covers right-edge Embla hairline */}
                      <div
                        aria-hidden
                        className='pointer-events-none absolute inset-y-0 right-0 z-20 w-0.5 bg-neutral-900'
                      />
                      <ShadcnCarouselContent
                        className='ml-0 [&>*]:!pl-0 [&>*]:!pt-0'
                        viewportClassName={cn(
                          'overflow-hidden [clip-path:inset(0_2px)] [contain:paint]',
                          viewportClassName,
                        )}
                      >
                        <CarouselSlideHeightContext.Provider
                          value={slideHeight}
                        >
                          <CarouselNextPeekContext.Provider value={peekContext}>
                            {children}
                          </CarouselNextPeekContext.Provider>
                        </CarouselSlideHeightContext.Provider>
                      </ShadcnCarouselContent>
                      {overlayDotsNode}
                    </div>
                    <CarouselDirectionButton
                      direction='next'
                      className={cn(
                        besideClassName,
                        'md:col-start-3 md:row-start-2',
                      )}
                    />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* hen-ui-allow-raw-html -- positioning box so edge arrows center on the slides, and the row for beside arrows */}
              <div
                className={cn(
                  'relative min-w-0',
                  arrowsBeside && 'flex items-center gap-sm',
                )}
              >
                {arrowsBeside && (
                  <CarouselDirectionButton
                    direction='previous'
                    className={besideClassName}
                  />
                )}
                <ShadcnCarouselContent
                  viewportClassName={cn(
                    arrowsBeside && 'flex-1',
                    shadowRoom,
                    viewportClassName,
                  )}
                >
                  <CarouselSlideHeightContext.Provider value={slideHeight}>
                    <CarouselNextPeekContext.Provider value={peekContext}>
                      {children}
                    </CarouselNextPeekContext.Provider>
                  </CarouselSlideHeightContext.Provider>
                </ShadcnCarouselContent>
                {arrowsBeside && (
                  <CarouselDirectionButton
                    direction='next'
                    className={besideClassName}
                  />
                )}
                {arrowsInside && (
                  <>
                    <CarouselDirectionButton
                      direction='previous'
                      className={cn(
                        arrowClassName,
                        insideArrowClassName,
                        insideInset.previous,
                        revealClassName,
                      )}
                    />
                    <CarouselDirectionButton
                      direction='next'
                      className={cn(
                        arrowClassName,
                        insideArrowClassName,
                        insideInset.next,
                        revealClassName,
                      )}
                    />
                  </>
                )}
                {overlayDotsNode}
              </div>
            </>
          )}
          <CarouselFooterNavigation
            arrowsInFooter={arrowsInFooter}
            footerDots={footerDots}
            arrowClassName={inFlowClassName}
            dotDesign={dotDesign}
            dotTone={dotTone}
            dotSize={dotSize}
            maxVisibleDots={maxVisibleDots}
            dotsInteractive={dotsInteractive}
            showBackground={resolvedShowDotBackground}
          />
        </CarouselArrowVisibility>
      </ShadcnCarousel>
    );
  },
);
Carousel.displayName = 'Carousel';

export const CarouselItem = React.forwardRef<HTMLDivElement, CarouselItemProps>(
  (
    { className, basis, 'basis-md': basisMd, 'basis-lg': basisLg, ...props },
    ref,
  ) => {
    const slideHeight = React.useContext(CarouselSlideHeightContext);
    const nextPeek = React.useContext(CarouselNextPeekContext);
    const slideId = React.useId();
    React.useLayoutEffect(() => {
      nextPeek.registerSlide(slideId, {
        basis,
        'basis-md': basisMd,
        'basis-lg': basisLg,
      });
      return () => nextPeek.registerSlide(slideId, null);
    }, [basis, basisLg, basisMd, nextPeek, slideId]);
    const peekClassName = carouselItemPeekClassName({
      basis,
      'basis-md': basisMd,
      'basis-lg': basisLg,
      active: nextPeek.active,
      slideCount: nextPeek.slideCount,
      rowExceeds: nextPeek.rowExceeds,
      peekOnPhone: nextPeek.peekOnPhone,
    });
    return (
      <ShadcnCarouselItem
        className={cn(
          carouselItemVariants({
            basis,
            'basis-md': basisMd,
            'basis-lg': basisLg,
          }),
          peekClassName,
          // A one-cell grid stretches the slide's content to the slide's full
          // height, even through display: contents wrappers. The minmax(0, 1fr)
          // column keeps text that doesn't wrap from widening the slide.
          slideHeight === 'equal' && 'grid grid-cols-1',
          className,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
CarouselItem.displayName = 'CarouselItem';

export type { CarouselApi };

export interface CarouselPreviousProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>,
    Omit<CarouselNavButtonVariantProps, 'size'> {}

export const CarouselPrevious = React.forwardRef<
  HTMLButtonElement,
  CarouselPreviousProps
>(({ className, type = 'button', design = 'default', ...props }, ref) => (
  <ShadcnButton
    ref={ref}
    type={type}
    variant='outline'
    size='icon'
    className={cn(carouselNavButtonVariants({ design }), className)}
    {...props}
  >
    <ArrowLeft className='h-4 w-4' aria-hidden='true' />
    <span className='sr-only'>Previous slide</span>
  </ShadcnButton>
));
CarouselPrevious.displayName = 'CarouselPrevious';

export interface CarouselNextProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>,
    Omit<CarouselNavButtonVariantProps, 'size'> {}

export const CarouselNext = React.forwardRef<
  HTMLButtonElement,
  CarouselNextProps
>(({ className, type = 'button', design = 'default', ...props }, ref) => (
  <ShadcnButton
    ref={ref}
    type={type}
    variant='outline'
    size='icon'
    className={cn(carouselNavButtonVariants({ design }), className)}
    {...props}
  >
    <ArrowRight className='h-4 w-4' aria-hidden='true' />
    <span className='sr-only'>Next slide</span>
  </ShadcnButton>
));
CarouselNext.displayName = 'CarouselNext';

type PillGeometryOptions = {
  /** `maxVisibleDots` is set: compact inactive dots match the active height. */
  capped?: boolean;
  /** Shrink the first inactive slot (more snaps before the window). */
  shrinkLeading?: boolean;
  /** Shrink the last inactive slot (more snaps after the window). */
  shrinkTrailing?: boolean;
};

/**
 * Capped indicators center each glyph in a slot this much wider than its
 * resting size, so dots keep an even pitch (12px compact) out to the capsule
 * edges even when edge dots taper. Slots are layout only, not tap targets.
 */
const CAPPED_INDICATOR_SLOT_PAD = 6;

function decorativePillGeometry(
  progress: number,
  count: number,
  size: CarouselDotSize,
  options: PillGeometryOptions = {},
): {
  dotTransforms: string[];
  dotWidths: number[];
  dotHeights: number[];
  trackWidth: number;
} {
  // Overflow tips use a slightly smaller diameter so the track hints at more
  // pages beyond the window.
  const compactInactive = options.capped === true ? 6 : 4;
  const inactiveFull = size === 'compact' ? compactInactive : 8;
  const inactiveTip = size === 'compact' ? 4 : 5;
  const activeWidth = size === 'compact' ? 12 : 20;
  const activeHeight = size === 'compact' ? 6 : 8;
  const capped = options.capped === true;
  const gap = capped ? 0 : 4;
  const clamped = Math.min(Math.max(progress, 0), Math.max(0, count - 1));
  const dotWidths: number[] = [];
  const dotHeights: number[] = [];
  const slotWidths: number[] = [];
  for (let index = 0; index < count; index += 1) {
    const proximity = Math.max(0, 1 - Math.abs(clamped - index));
    if (proximity > 0) {
      const width = inactiveFull + (activeWidth - inactiveFull) * proximity;
      dotWidths.push(width);
      dotHeights.push(inactiveFull + (activeHeight - inactiveFull) * proximity);
      slotWidths.push(capped ? width + CAPPED_INDICATOR_SLOT_PAD : width);
      continue;
    }
    const atLeadingTip = options.shrinkLeading === true && index === 0;
    const atTrailingTip =
      options.shrinkTrailing === true && index === count - 1;
    const diameter = atLeadingTip || atTrailingTip ? inactiveTip : inactiveFull;
    dotWidths.push(diameter);
    dotHeights.push(diameter);
    slotWidths.push(
      capped ? inactiveFull + CAPPED_INDICATOR_SLOT_PAD : diameter,
    );
  }

  let x = 0;
  const dotTransforms = dotWidths.map((width, index) => {
    const slot = slotWidths[index] ?? width;
    const transform = `translate3d(${x + (slot - width) / 2}px, -50%, 0)`;
    x += slot + gap;
    return transform;
  });

  return {
    dotTransforms,
    dotWidths,
    dotHeights,
    trackWidth:
      slotWidths.reduce((sum, width) => sum + width, 0) +
      Math.max(0, count - 1) * gap,
  };
}

type PillStripGeometry = {
  start: number;
  end: number;
  /** Decorative glyph offsets in px; interactive cells lay out in flow. */
  glyphOffsets: number[];
  dotWidths: number[];
  dotHeights: number[];
  /** Window width in px, without the `px-xxs` padding of standard cells. */
  viewportPx: number;
  /** CSS translate (negative) that brings the window to the leading edge. */
  shift: string;
  /** CSS length of the visible window. */
  viewportWidth: string;
};

/**
 * Lays every snap out on one strip and returns how far to shift it so only
 * the visible window shows. Each dot stays mounted under its own snap index,
 * so a window change slides the strip instead of relabeling the dots.
 * Standard interactive cells keep the `px-xxs` padding from
 * `carouselPillDotCellVariants`, so their lengths add that token in CSS and
 * follow a tenant's spacing overrides.
 */
function pillStripGeometry(
  count: number,
  activeIndex: number,
  size: CarouselDotSize,
  windowStart: number,
  windowSize: number,
  interactive: boolean,
): PillStripGeometry {
  const compact = size === 'compact';
  const inactiveFull = compact ? 6 : 8;
  const inactiveMid = compact ? 5 : 6.5;
  const inactiveTip = compact ? 4 : 5;
  const activeWidth = compact ? 12 : 20;
  const activeHeight = compact ? 6 : 8;
  const start = Math.min(Math.max(0, windowStart), Math.max(0, count - 1));
  const end = Math.min(count - 1, start + Math.max(1, windowSize) - 1);
  const span = end - start;
  const depth = Math.min(2, Math.floor(span / 2));
  const taperSizes =
    depth === 2
      ? [inactiveFull, inactiveMid, inactiveTip]
      : [inactiveFull, inactiveTip];
  const activeSlot = activeIndex - start;
  const headRoom = Math.min(Math.max(0, activeSlot), depth);
  const tailRoom = Math.min(Math.max(0, span - activeSlot), depth);

  const cellWidths: number[] = [];
  const cellOffsets: number[] = [];
  const glyphOffsets: number[] = [];
  const dotWidths: number[] = [];
  const dotHeights: number[] = [];
  const paddedCells = interactive && !compact;
  let x = 0;
  for (let index = 0; index < count; index += 1) {
    let width: number;
    let height: number;
    if (index === activeIndex) {
      width = activeWidth;
      height = activeHeight;
    } else {
      // Dots shrink toward a window edge by how far they sit from it, limited
      // by how far the active dot is from that edge.
      const taper =
        index < start || index > end
          ? depth
          : Math.max(0, headRoom - (index - start), tailRoom - (end - index));
      width = taperSizes[taper] ?? inactiveTip;
      height = width;
    }
    // Compact cells are `min-w-6 px-0`, a fixed 24px target (WCAG 2.5.8).
    // Standard cells are the glyph plus `px-xxs` on each side, added below.
    const cell = interactive
      ? compact
        ? 24
        : width
      : (index === activeIndex ? activeWidth : inactiveFull) +
        CAPPED_INDICATOR_SLOT_PAD;
    dotWidths.push(width);
    dotHeights.push(height);
    cellWidths.push(cell);
    cellOffsets.push(x);
    glyphOffsets.push(x + (cell - width) / 2);
    x += cell;
  }

  const offsetPx = cellOffsets[start] ?? 0;
  const viewportPx =
    (cellOffsets[end] ?? 0) + (cellWidths[end] ?? 0) - offsetPx;
  const length = (px: number, cells: number, sign: 1 | -1 = 1) =>
    paddedCells
      ? `calc(${sign * px}px ${sign === 1 ? '+' : '-'} ${cells * 2} * var(--space-xxs, 8px))`
      : `${sign * px}px`;

  return {
    start,
    end,
    glyphOffsets,
    dotWidths,
    dotHeights,
    viewportPx,
    shift: length(offsetPx, start, -1),
    viewportWidth: length(viewportPx, end - start + 1),
  };
}

const pillStripMotionClassName =
  'duration-slow ease-emphasized motion-reduce:transition-none';

/** `useLayoutEffect` in the browser; `useEffect` on the server to avoid SSR warnings. */
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

export interface CarouselDotsProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
    CarouselDotVariantProps {
  count: number;
  activeIndex: number;
  onDotClick?: (index: number) => void;
  /**
   * Absolute snap indices for each rendered dot. When omitted, dots map to
   * `0..count-1`. Labels and click callbacks use these absolute indices.
   */
  dotIndices?: number[];
  /**
   * Total snap/page count for non-interactive group labels. Defaults to
   * `count` when omitted (full track).
   */
  totalCount?: number;
  /**
   * First snap of the visible window for `pill` dots. Set together with
   * `windowSize` to render every snap on a strip that slides inside the
   * capsule; dots outside the window are clipped and skipped by assistive
   * technology.
   */
  windowStart?: number;
  /**
   * Number of `pill` dots visible at once. Requires `windowStart`. Setting it
   * opts into the capped pill look (compact inactive dots match the active
   * height, and dots animate) even when every snap fits.
   */
  windowSize?: number;
  /** Whether each dot is a page-selection button. Omitted means true. */
  interactive?: boolean;
  /** Rounded capsule behind `pill` dots, contrasting with `tone`. */
  showBackground?: boolean;
}

export const CarouselDots = React.forwardRef<HTMLDivElement, CarouselDotsProps>(
  (
    {
      className,
      count,
      activeIndex,
      onDotClick,
      dotIndices,
      totalCount,
      windowStart,
      windowSize,
      design = 'default',
      tone = 'light',
      size = 'default',
      interactive = true,
      showBackground = false,
      style,
      ...props
    },
    ref,
  ) => {
    const decorativePill = !interactive && design === 'pill';
    const pageTotal = totalCount ?? count;
    const strip =
      design === 'pill' &&
      count > 0 &&
      windowStart !== undefined &&
      windowSize !== undefined &&
      windowSize < count
        ? pillStripGeometry(
            count,
            activeIndex,
            size ?? 'default',
            windowStart,
            windowSize,
            interactive,
          )
        : undefined;
    const firstIndex = dotIndices?.[0] ?? 0;
    const lastIndex = dotIndices?.[count - 1] ?? Math.max(0, count - 1);
    const windowed = pageTotal > count && count > 0;
    const capped = windowSize !== undefined || windowed;
    const pillGeometry =
      design === 'pill' && count > 0 && !strip
        ? decorativePillGeometry(activeIndex, count, size ?? 'default', {
            capped,
            shrinkLeading: windowed && firstIndex > 0,
            shrinkTrailing: windowed && lastIndex < pageTotal - 1,
          })
        : undefined;
    const absoluteActive = dotIndices?.[activeIndex] ?? activeIndex;
    const decorativeTrackWidth = strip?.viewportPx ?? pillGeometry?.trackWidth;
    const visibleStart = strip?.start ?? firstIndex;
    const visibleEnd = strip?.end ?? lastIndex;

    const trackRef = React.useRef<HTMLDivElement | null>(null);
    const setTrackRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        trackRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );
    const focusedSnapRef = React.useRef<number | null>(null);

    // When the window shifts past a focused dot, the dot is clipped and
    // aria-hidden (pill strip) or unmounted (solid / outline). Move focus to
    // the active dot so keyboard users never sit on a hidden control, but
    // leave focus alone if it already moved elsewhere on the page.
    useIsomorphicLayoutEffect(() => {
      const track = trackRef.current;
      const focusedSnap = focusedSnapRef.current;
      if (!interactive || !track || focusedSnap === null) {
        return;
      }
      if (focusedSnap >= visibleStart && focusedSnap <= visibleEnd) {
        return;
      }
      const focused = track.ownerDocument.activeElement;
      const focusLost =
        focused === null || focused === track.ownerDocument.body;
      if (!focusLost && !track.contains(focused)) {
        return;
      }
      track
        .querySelector<HTMLButtonElement>('button[aria-current="page"]')
        ?.focus({ preventScroll: true });
    }, [interactive, visibleStart, visibleEnd]);

    const dotNodes = Array.from({ length: count }).map((_, index) => {
      const absoluteIndex = dotIndices?.[index] ?? index;
      const active = activeIndex === index;
      const label = `Go to slide ${absoluteIndex + 1}`;
      const handleClick = () => onDotClick?.(absoluteIndex);
      const handleFocus = () => {
        focusedSnapRef.current = absoluteIndex;
      };
      const handleBlur = (event: React.FocusEvent<HTMLButtonElement>) => {
        const button = event.currentTarget;
        // Browsers can blur a focused node while removing it; only a dot that
        // is still mounted means focus really left the dots.
        queueMicrotask(() => {
          if (button.isConnected && focusedSnapRef.current === absoluteIndex) {
            focusedSnapRef.current = null;
          }
        });
      };
      const outsideWindow =
        strip !== undefined && (index < strip.start || index > strip.end);
      const glyphWidth =
        strip?.dotWidths[index] ?? pillGeometry?.dotWidths[index];
      const glyphHeight =
        strip?.dotHeights[index] ?? pillGeometry?.dotHeights[index];
      const glyphClassName = cn(
        carouselDotVariants({
          design,
          tone,
          active,
          size,
        }),
        design === 'pill' && capped && pillStripMotionClassName,
      );
      if (!interactive) {
        if (design === 'pill') {
          return (
            <React.Fragment key={absoluteIndex}>
              {/* hen-ui-allow-raw-html -- decorative page-position glyph needs no interactive hit cell */}
              <span
                aria-hidden
                data-slot='carousel-dot-glyph'
                className={cn(
                  glyphClassName,
                  'pointer-events-none absolute left-0 top-1/2 will-change-[transform,width] motion-reduce:transition-none',
                )}
                style={{
                  transform: strip
                    ? `translate3d(${strip.glyphOffsets[index]}px, -50%, 0)`
                    : pillGeometry?.dotTransforms[index],
                  width: glyphWidth,
                  height: glyphHeight,
                }}
              />
            </React.Fragment>
          );
        }
        return (
          <React.Fragment key={absoluteIndex}>
            {/* hen-ui-allow-raw-html -- decorative page-position glyph; navigation stays with arrows, keyboard, and swipe */}
            <span
              aria-hidden
              data-slot='carousel-dot-glyph'
              className={cn(glyphClassName, 'pointer-events-none')}
            />
          </React.Fragment>
        );
      }
      // Pill always uses a hit cell. Compact default/outline wrap the glyph
      // so the visible dot can shrink while the tap target stays ≥24px.
      if (design === 'pill') {
        return (
          <React.Fragment key={absoluteIndex}>
            {/* hen-ui-allow-raw-html -- pagination control with aria-current; not a content Button */}
            <button
              type='button'
              aria-current={active ? 'page' : undefined}
              aria-label={label}
              aria-hidden={outsideWindow || undefined}
              tabIndex={outsideWindow ? -1 : undefined}
              className={cn(
                carouselPillDotCellVariants({ tone, size }),
                strip && 'shrink-0',
                outsideWindow && 'pointer-events-none',
              )}
              onClick={handleClick}
              onFocus={handleFocus}
              onBlur={handleBlur}
            >
              {/* hen-ui-allow-raw-html -- decorative glyph only; label lives on the button */}
              <span
                aria-hidden
                data-slot='carousel-dot-glyph'
                className={cn(glyphClassName, 'motion-reduce:transition-none')}
                style={{ width: glyphWidth, height: glyphHeight }}
              />
            </button>
          </React.Fragment>
        );
      }
      if (size === 'compact') {
        return (
          <React.Fragment key={absoluteIndex}>
            {/* hen-ui-allow-raw-html -- pagination control with aria-current; hit cell wraps a smaller glyph */}
            <button
              type='button'
              aria-current={active ? 'page' : undefined}
              aria-label={label}
              className={carouselCompactDotCellClassName}
              onClick={handleClick}
              onFocus={handleFocus}
              onBlur={handleBlur}
            >
              {/* hen-ui-allow-raw-html -- decorative glyph only; label lives on the button */}
              <span
                aria-hidden
                data-slot='carousel-dot-glyph'
                className={glyphClassName}
              />
            </button>
          </React.Fragment>
        );
      }
      return (
        <React.Fragment key={absoluteIndex}>
          {/* hen-ui-allow-raw-html -- pagination control directly represents the default dot glyph */}
          <button
            type='button'
            aria-current={active ? 'page' : undefined}
            aria-label={label}
            className={glyphClassName}
            onClick={handleClick}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </React.Fragment>
      );
    });

    const stripTransform = strip
      ? `translate3d(${strip.shift}, 0, 0)`
      : undefined;

    return (
      <>
        {/* hen-ui-allow-raw-html -- indicator track owns absolute glyph geometry for the pill selection transition */}
        <div
          ref={setTrackRef}
          role={interactive ? 'group' : 'img'}
          aria-label={
            interactive
              ? 'Slide navigation'
              : `Page ${absoluteActive + 1} of ${pageTotal}`
          }
          className={cn(
            carouselDotsTrackVariants({ design, size }),
            !interactive && !decorativePill && 'gap-min',
            decorativePill && 'relative',
            decorativePill &&
              strip &&
              cn('transition-[width]', pillStripMotionClassName),
            className,
          )}
          style={{
            ...style,
            ...(decorativePill && decorativeTrackWidth !== undefined
              ? { width: decorativeTrackWidth }
              : {}),
          }}
          {...props}
        >
          {design === 'pill' && showBackground ? (
            <>
              {/* hen-ui-allow-raw-html -- absolute capsule; does not affect dot layout */}
              <div
                aria-hidden
                data-slot='carousel-dots-background'
                className={carouselDotsBackgroundVariants({ tone })}
              />
            </>
          ) : null}
          {strip ? (
            <>
              {/* hen-ui-allow-raw-html -- clips the sliding strip to the visible window; the vertical bleed keeps 24px hit areas and focus rings */}
              <div
                data-slot='carousel-dots-viewport'
                className={cn(
                  '[clip-path:inset(-12px_0)]',
                  decorativePill
                    ? // In-flow height matches the tallest glyph so track py-*
                      // pads around it. Absolute inset would collapse to the
                      // padding alone and leave glyphs without capsule clearance.
                      cn(
                        'relative w-full',
                        size === 'compact' ? 'h-1.5' : 'h-2',
                      )
                    : cn(
                        'relative transition-[width]',
                        pillStripMotionClassName,
                      ),
                )}
                style={
                  decorativePill ? undefined : { width: strip.viewportWidth }
                }
              >
                {/* hen-ui-allow-raw-html -- every snap's dot on one strip, shifted so the window shows */}
                <div
                  data-slot='carousel-dots-strip'
                  className={cn(
                    'transition-transform',
                    pillStripMotionClassName,
                    decorativePill
                      ? 'absolute inset-y-0 left-0'
                      : 'flex w-max items-center',
                  )}
                  style={{ transform: stripTransform }}
                >
                  {dotNodes}
                </div>
              </div>
            </>
          ) : (
            dotNodes
          )}
        </div>
      </>
    );
  },
);
CarouselDots.displayName = 'CarouselDots';

/** Dots wired to the Embla carousel context (used by the high-level Carousel). */
function CarouselDotsConnected({
  design = 'default',
  tone = 'light',
  size = 'default',
  maxVisibleDots,
  interactive = true,
  showBackground = false,
}: {
  design?: CarouselDotDesign;
  tone?: CarouselDotTone;
  size?: CarouselDotSize;
  maxVisibleDots?: number;
  interactive?: boolean;
  showBackground?: boolean;
}) {
  const { scrollTo, selectedIndex, scrollSnaps } = useCarousel();
  const { visibleIndices, activeInWindow, capped } = resolveVisibleDotWindow(
    scrollSnaps.length,
    selectedIndex,
    maxVisibleDots,
  );
  if (design === 'pill' && capped) {
    return (
      <CarouselDots
        count={scrollSnaps.length}
        activeIndex={selectedIndex}
        windowStart={visibleIndices[0] ?? 0}
        windowSize={visibleIndices.length}
        onDotClick={scrollTo}
        design={design}
        tone={tone}
        size={size}
        interactive={interactive}
        showBackground={showBackground}
      />
    );
  }
  return (
    <CarouselDots
      count={visibleIndices.length}
      activeIndex={activeInWindow}
      dotIndices={visibleIndices}
      totalCount={scrollSnaps.length}
      onDotClick={scrollTo}
      design={design}
      tone={tone}
      size={size}
      interactive={interactive}
      showBackground={showBackground}
    />
  );
}
